/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CourseInformation
 *
 * @file CourseInformation.jsx
 * @author Mohamed yasar arabath M
 * @since 1.0.0
 */

import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import {
  Section,
  Inner,
  Course,
  TitleWrapper,
  MetaInfoContainer,
  Metadetails,
  Label,
  Metavalue,
  Divider,
  Book,
  Figure,
  Img,
  Meta,
  BookInfo,
  BookTitle,
  Authors,
  ButtonSecondary,
  OpenBookButton,
  TextContainer,
  Label2,
  InstructorContainer,
  Avatar,
  InstructorName,
  ActionButtonsContainer,
  IconButtonStyled,
  TertiaryButton
} from './styles/CourseInformationStyles';


const CourseInformation = (props) => {
  const { data, actions, config = {} } = props;

  const {
    title = null,
    metaData = [],
    subTitle = null,
    authors = null,
    coverImage = '../../assets/images/coverThumbnail.jpg',
    instructor = null
  } = data;

  const {
    openBook = {},
    accessEducatorGuides = {},
    settings = {}
  } = actions;

  const renderInstructor = () => {
    if (!instructor || !config.showInstructor) {
      return null;
    }

    const {
      avatar,
      name
    } = instructor;

    return (
      <InstructorContainer>
        <Avatar src={avatar} alt="Instructor avatar" />

        <InstructorName>
          {name}
        </InstructorName>
      </InstructorContainer>
    );
  };

  const renderButton = (btn = {}) => {
    if (!btn.callback) {
      return null;
    }

    const {
      icon,
      text,
      callback
    } = btn;

    if (icon) {
      return (
        <IconButtonStyled onClick={callback}>
          {icon}
        </IconButtonStyled>
      );
    }

    return (
      <TertiaryButton onClick={callback}>
        {text}
      </TertiaryButton>
    );
  };

  const renderActions = () => {
    const listOfActions = [accessEducatorGuides, settings];
    const haveActions = listOfActions.some((action) => action.callback);

    return (haveActions ? (
      <ActionButtonsContainer>
        {listOfActions.map((action, index) => (
          <Fragment key={action.text + index}>
            {renderButton(action)}
            {(index !== (listOfActions.length - 1)) ? <Divider /> : null}
          </Fragment>
        ))}
      </ActionButtonsContainer>
    ) : null);
  };

  const renderOpenBookButton = () => {
    if (!openBook.text || !openBook.callback) {
      return null;
    }

    if (config.openBookBorderless) {
      return (
        <OpenBookButton
          variant="text"
          onClick={openBook.callback}
        >
          <TextContainer>
            <Label2> {openBook.text} </Label2>
          </TextContainer>
        </OpenBookButton>
      );
    }

    return (
      <ButtonSecondary onClick={openBook.callback}>
        <TextContainer>
          <Label2> {openBook.text} </Label2>
        </TextContainer>
      </ButtonSecondary>
    );
  };

  return (
    <Section>
      <Inner>
        <Course>
          <TitleWrapper>
            {title}
          </TitleWrapper>
          <MetaInfoContainer>
            {config.showMetaData && metaData && metaData.map((meta, index) => (
              <Fragment key={index}>
                <Metadetails>
                  <Label> {meta.title} </Label>
                  <Metavalue> {meta.info} </Metavalue>
                </Metadetails>
                {(index !== (metaData.length - 1)) ? <Divider /> : null}
              </Fragment>
            ))}

            {renderInstructor()}
            {renderActions()}
          </MetaInfoContainer>
        </Course>
        <Book>
          <Figure>
            <Img alt="" src={coverImage} />
          </Figure>
          <Meta>
            <BookInfo>
              <BookTitle> {subTitle} </BookTitle>
              <Authors> {authors} </Authors>
            </BookInfo>
            {renderOpenBookButton()}
          </Meta>
        </Book>
      </Inner>
    </Section>);
};


CourseInformation.propTypes = {
  data: PropTypes.object,
  actions: PropTypes.object,
  config: PropTypes.object
};

CourseInformation.defaultProps = {
  data: {
    title: 'The Human Experience: An Introduction to the Humanities',
    metaData: [
      {
        title: 'Instructor:',
        info: 'Dr. Ivan Pavlov'
      },
      {
        title: 'Start date:',
        info: '7/18/2025'
      },
      {
        title: 'End date:',
        info: '11/19/2025'
      }
    ],
    subTitle: 'About Philosophy, Pearson New International Edition, 11th edition',
    authors: 'Robert Paul Wolff',
    coverImage: '../../assets/images/coverThumbnail.jpg'
  },
  actions: {
    openBook: {
      text: 'Open eTextbook',
      callback: () => {}
    }
  },
  config: {}
};

export default CourseInformation;
